<template>
  <fragment>
    <AppBody
      title="Afiliados"
      :subtitle="$t('sales.affiliates')"
      :actions="actions"
    >
      <CardModule untitle>
        <ExpansionPanelFilters :title="$t('sales.affiliate_filter')">
          <v-form @submit.prevent="filterAffiliates" class="search-forms">
            <v-row>
              <v-col cols="12" md="3" sm="4">
                <BaseTextField
                  :label="$t('sales.affiliate_name')"
                  :placeholder="$t('sales.affiliate_name_placeholder')"
                  v-model="filter.name"
                >
                </BaseTextField>
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <BaseSelect
                  label="Status"
                  :placeholder="$t('sales.affiliate_status_placeholder')"
                  :items="status"
                  item-text="text"
                  item-value="value"
                  v-model="filter.status"
                  clearable
                ></BaseSelect>
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <BaseAutocomplete
                  v-if="isAdmin"
                  v-model="filter.producer"
                  :label="$t('customers.producer')"
                  :placeholder="$t('customers.select-producer')"
                  :items="storeList"
                  item-text="nome_loja"
                  item-value="sequenciador"
                  clearable
                ></BaseAutocomplete>
              </v-col>
              <v-col class="d-flex justify-end align-end">
                <BaseButton
                  variant="filter"
                  type="submit"
                  :loading="affiliatesLoading"
                >
                  {{ $t("sales.affiliate_apply_filter") }}
                </BaseButton>
              </v-col>
            </v-row>
          </v-form>
        </ExpansionPanelFilters>

        <v-data-table
          class="table-body"
          :headers="headerFilterByUserType"
          :items="allAffiliates"
          :loading="affiliatesLoading"
          :no-data-text="$t('sales.no_affiliate')"
          :loading-text="$t('sales.affiliate_loading')"
          item-key="idafiliado"
          :items-per-page="10"
          :footer-props="{
            itemsPerPageOptions: [10, 20, 50],
            itemsPerPageText: $t('sales.affiliate_by_page'),
          }"
        >
          <template v-if="isAdmin" v-slot:item.actions="{ item }">
            <div style="display: flex; justify-content: center; gap: 24px">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="$config.dados.corinputs"
                    height="10"
                    width="10"
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="openDetailsDialog(item.idafiliado, item.slug)"
                  >
                    <v-icon>mdi-table-large</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("reports.sales-by-affiliate.details") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="$config.dados.corinputs"
                    height="10"
                    width="10"
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="loginAsAffiliate(item.email)"
                  >
                    <v-icon>mdi-login</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("customers.enter") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="$config.dados.corinputs"
                    height="10"
                    width="10"
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="openChangeStatusModal(item)"
                  >
                    <v-icon>mdi-account-arrow-down-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("sales.affiliate_change_status") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="$config.dados.corinputs"
                    height="10"
                    width="10"
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="openEditModal(item)"
                  >
                    <v-icon>mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("common.edit") }}</span>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              style="align-items: center; justify-content: center"
              :color="item.status == 'ATIVO' ? 'success' : 'error'"
              dark
              small
            >
              {{ item.status }}
            </v-chip>
          </template>
          <template v-slot:item.nome="{ item }">
            {{ item.nome || "-" }}
          </template>
          <template v-slot:item.cpf="{ item }">
            {{ item.cpf || "-" }}
          </template>
          <template v-slot:item.telefone="{ item }">
            {{ item.telefone || "-" }}
          </template>
        </v-data-table>
      </CardModule>
    </AppBody>
    <ConfirmDialog
      :dialogText="$t('sales.affiliate_change_status_confirmation')"
      @callback="changeStatusAffiliate"
    />
    <AffiliatesSettings
      v-if="dialogPreRegister"
      :affiliateData="affiliateData"
      @close="affiliateData = null"
      @submit="affiliateData = null"
    />
    <AffiliateDetails
      v-if="detailsDialog"
      v-model="detailsDialog"
      :affiliateId="affiliateId"
      :affiliateSlug="affiliateSlug"
      @close="detailsDialog = false"
    />
  </fragment>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import AppBody from "@/layouts/components/AppBody.vue";
import CardModule from "@/components/cards/CardModule.vue";
import BaseSelect from "@/components/forms/BaseSelect.vue";
import BaseTextField from "@/components/forms/BaseTextField.vue";
import BaseAutocomplete from "../../components/forms/BaseAutocomplete.vue";
import BaseButton from "@/components/forms/BaseButton.vue";
import ConfirmDialog from "@/components/global/ConfirmDialog.vue";
import AffiliatesSettings from "./AffiliatesSettings.vue";
import ExpansionPanelFilters from "@/components/forms/ExpansionPanelFilters.vue";
import AffiliateDetails from "../../components/pessoas/AffiliateDetails.vue";

export default {
  name: "Affiliates",
  components: {
    AppBody,
    BaseButton,
    CardModule,
    BaseSelect,
    BaseAutocomplete,
    BaseTextField,
    ConfirmDialog,
    AffiliatesSettings,
    ExpansionPanelFilters,
    AffiliateDetails,
  },
  metaInfo: {
    title: "Afiliados | ElevenTickets",
    meta: [
      {
        content: "Afiliados | ElevenTickets",
      },
    ],
  },
  data() {
    return {
      isDeleting: false,
      itemDeleting: null,
      affiliateData: null,
      detailsDialog: false,
      affiliateId: "",
      affiliateSlug: "",
      filter: {
        name: "",
        status: "",
        producer: "",
      },
    };
  },
  computed: {
    ...mapGetters([
      "allAffiliates",
      "affiliatesLoading",
      "storeSequencial",
      "dialogPreRegister",
      "isAdmin",
      "storeList",
    ]),
    status() {
      return [
        { text: this.$t("sales.affiliate_active"), value: "ATIVO" },
        { text: this.$t("sales.affiliate_inactive"), value: "INATIVO" },
      ];
    },
    headers() {
      return [
        {
          text: this.$t("sales.affiliate_name"),
          align: "center",
          sortable: true,
          value: "nome",
          width: "200px",
        },
        {
          text: this.$t("sales.affiliate_email"),
          align: "center",
          sortable: true,
          value: "email",
          width: "90px",
        },
        {
          text: this.$t("sales.affiliate_cpf"),
          align: "center",
          sortable: true,
          value: "cpf",
          width: "120px",
        },
        {
          text: this.$t("sales.affiliate_phone"),
          align: "center",
          sortable: true,
          value: "telefone",
          width: "120px",
        },
        {
          text: "Status",
          align: "center",
          sortable: true,
          value: "status",
          width: "120px",
        },
      ];
    },
    actions() {
      return [
        {
          name: this.$t("sales.new_affiliate"),
          action: () => {
            this.setDialogPreRegister(!this.dialogPreRegister);
          },
        },
      ];
    },
    headerFilterByUserType() {
      if (this.isAdmin) {
        return [
          ...this.headers,
          {
            text: this.$t("sales.affiliate_actions"),
            align: "center",
            sortable: false,
            value: "actions",
            width: "120px",
          },
        ];
      }
      return this.headers;
    },
  },
  methods: {
    ...mapActions([
      "fetchAllAffiliates",
      "changeAffiliateStatus",
      "getAffiliateLogin",
    ]),
    ...mapMutations(["setDialogPreRegister"]),
    filterAffiliates() {
      let filters = {
        loja: this.isAdmin ? this.filter.producer || "" : this.storeSequencial,
        limite: 10000,
        pagina: 1,
        name: this.filter.name,
        status: this.filter.status,
      };
      this.fetchAllAffiliates(filters);
    },
    openChangeStatusModal(item) {
      this.$config.confirmDialog = true;
      this.itemDeleting = item;
    },
    changeStatusAffiliate() {
      let filter = {
        id: this.itemDeleting.idafiliado,
        status: this.itemDeleting.status === "ATIVO" ? "INATIVO" : "ATIVO",
      };
      this.changeAffiliateStatus(filter).then(() => {
        this.$config.confirmDialog = false;
      });
    },
    openEditModal(affiliate) {
      this.setDialogPreRegister(!this.dialogPreRegister);
      this.affiliateData = affiliate;
    },
    openDetailsDialog(affiliateId, affiliateSlug) {
      this.affiliateId = affiliateId;
      this.affiliateSlug = affiliateSlug;
      this.detailsDialog = true;
    },
    loginAsAffiliate(email) {
      this.getAffiliateLogin(email);
    },
  },
  mounted() {
    this.filterAffiliates();
  },
};
</script>

<style>
.v-dialog {
  overflow-y: unset !important;
  border-radius: 50px !important;
}
.modal-header {
  padding: 15px;
}
</style>
